<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center px-4 sm:px-6">
    <div class="absolute inset-0 bg-slate-900/90 backdrop-blur-sm" @click="$emit('close')"></div>
    
    <div class="relative w-full max-w-7xl max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col">
      <!-- Header -->
      <div class="flex justify-between items-center p-6 border-b border-slate-100">
        <h2 class="text-2xl font-bold text-slate-900">All Projects</h2>
        <button @click="$emit('close')" class="p-2 hover:bg-slate-100 rounded-full transition-colors">
          <svg class="w-6 h-6 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Scrollable Content -->
      <div class="flex-1 overflow-y-auto p-6 sm:p-8">
        <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div v-for="project in projects" :key="project.id" 
               class="group bg-white rounded-2xl border border-slate-100 overflow-hidden hover:shadow-xl hover:shadow-slate-200/50 transition-all duration-300">
            <!-- Project Media -->
            <div class="relative h-52 bg-slate-900 overflow-hidden cursor-pointer" @click="$emit('open-details', project)">
              <img v-if="project.mediaType === 'image'" 
                   :src="project.media" 
                   :alt="project.title" 
                   @click.stop="$emit('view-image', project.media)"
                   class="w-full h-full object-contain bg-slate-900 group-hover:scale-105 transition-transform duration-500 cursor-zoom-in" />
              <div v-else-if="project.mediaType === 'video'" class="relative w-full h-full">
                <video :src="project.media" 
                       class="w-full h-full object-cover" 
                       muted loop></video>
                <div class="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/40 transition-colors">
                  <div class="w-12 h-12 bg-white/30 backdrop-blur-md rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                    <svg class="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                </div>
              </div>
              <div class="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent pointer-events-none"></div>
            </div>
            
            <!-- Project Info -->
            <div class="p-6">
              <h3 class="text-xl font-bold text-slate-900 mb-2 group-hover:text-emerald-600 transition-colors">
                {{ project.title }}
              </h3>
              <p class="text-slate-600 text-sm mb-4 clamp-2 leading-relaxed">
                {{ project.shortDescription }}
              </p>
              <div class="flex flex-wrap gap-2 mb-5">
                <span v-for="tech in project.technologies.slice(0, 3)" 
                      :key="tech" 
                      class="px-3 py-1 bg-slate-100 text-slate-600 text-xs font-medium rounded-full">
                  {{ tech }}
                </span>
              </div>
              <div class="flex gap-3">
                <a v-if="project.link" 
                   :href="project.link" 
                   target="_blank" 
                   class="flex-1 px-4 py-2.5 bg-emerald-500 text-white text-center text-sm rounded-lg font-semibold hover:bg-emerald-600 transition-colors">
                  View Live
                </a>
                <button @click="$emit('open-details', project)" 
                        class="flex-1 px-4 py-2.5 bg-slate-100 text-slate-700 text-sm rounded-lg font-semibold hover:bg-slate-200 transition-colors">
                  Details
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable no-undef */
import { onMounted, onUnmounted } from 'vue'
import { projects } from '../data/portfolio.js'

defineEmits(['close', 'open-details', 'view-image'])

const handleEscape = (e) => {
  if (e.key === 'Escape') {
    emit('close')
  }
}

// Lock scroll and handle escape
onMounted(() => {
  document.addEventListener('keydown', handleEscape)
  document.body.style.overflow = 'hidden'
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleEscape)
  document.body.style.overflow = ''
})
</script>
